import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Header, MobileNav } from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { requestsAPI } from '../../lib/api';
import { getServiceName, formatDate, formatDateTime, REQUEST_STATUS, formatPrice } from '../../lib/constants';
import { CalendarBlank, ChatCircle, Clock, MapPin, Dog, CheckCircle, XCircle, CurrencyEur } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

export default function ProviderRequestsPage() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');

  const fetchRequests = useCallback(async () => {
    try {
      const data = await requestsAPI.getRequests();
      setRequests(data);
    } catch (error) {
      toast.error('Erro ao carregar pedidos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleAcceptRequest = async (requestId, price) => {
    try {
      await requestsAPI.updateStatus(requestId, 'accepted', price || null);
      toast.success('Pedido aceite!');
      fetchRequests();
    } catch (error) {
      toast.error('Erro ao aceitar pedido');
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await requestsAPI.updateStatus(requestId, 'rejected');
      toast.success('Pedido recusado');
      fetchRequests();
    } catch (error) {
      toast.error('Erro ao recusar pedido');
    }
  };

  const handleCompleteRequest = async (requestId) => {
    try {
      await requestsAPI.updateStatus(requestId, 'completed');
      toast.success('Serviço marcado como concluído!');
      fetchRequests();
    } catch (error) {
      toast.error('Erro ao concluir serviço');
    }
  };

  const filteredRequests = requests.filter(req => {
    if (activeTab === 'all') return true;
    return req.status === activeTab;
  });

  const RequestCard = ({ request }) => {
    const client = request.client || {};
    const pets = request.pets_details || [];
    const status = REQUEST_STATUS[request.status] || { label: request.status, color: 'bg-gray-100' };
    const [priceInput, setPriceInput] = useState('');
    const [showPriceDialog, setShowPriceDialog] = useState(false);

    return (
      <Card className="provider-card">
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <Avatar>
                <AvatarImage src={client.picture} />
                <AvatarFallback>{client.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <div>
                <h3 className="font-semibold">{client.name}</h3>
                <Badge className={`${status.color} text-xs`}>
                  {status.label}
                </Badge>
              </div>
            </div>
            {request.price && (
              <span className="font-bold text-primary">{formatPrice(request.price)}</span>
            )}
          </div>

          <div className="space-y-2 text-sm text-muted-foreground">
            <p className="flex items-center gap-2">
              <Dog className="w-4 h-4" />
              {getServiceName(request.service_type)}
            </p>
            <p className="flex items-center gap-2">
              <CalendarBlank className="w-4 h-4" />
              {formatDateTime(request.start_date)}
              {request.end_date && ` - ${formatDate(request.end_date)}`}
            </p>
            <p className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              {request.address}
            </p>
            {pets.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs">Animais:</span>
                {pets.map(pet => (
                  <Badge key={pet.pet_id} variant="outline" className="text-xs">
                    {pet.name} ({pet.breed || pet.species})
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {request.observations && (
            <div className="mt-3 p-2 bg-muted/50 rounded text-sm">
              <span className="font-medium">Observações:</span> {request.observations}
            </div>
          )}

          <div className="flex gap-2 mt-4">
            {request.status === 'pending' && (
              <>
                <Dialog open={showPriceDialog} onOpenChange={setShowPriceDialog}>
                  <DialogTrigger asChild>
                    <Button className="flex-1 rounded-full" data-testid={`accept-btn-${request.request_id}`}>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Aceitar
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Definir Preço</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <label className="text-sm font-medium mb-2 block">Preço Total (€)</label>
                        <Input
                          type="number"
                          value={priceInput}
                          onChange={(e) => setPriceInput(e.target.value)}
                          placeholder="Ex: 25.00"
                          data-testid="price-input"
                        />
                      </div>
                      <Button
                        onClick={() => {
                          handleAcceptRequest(request.request_id, parseFloat(priceInput) || null);
                          setShowPriceDialog(false);
                        }}
                        className="w-full rounded-full"
                      >
                        Confirmar e Aceitar
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
                <Button 
                  variant="outline"
                  onClick={() => handleRejectRequest(request.request_id)}
                  className="flex-1 rounded-full"
                  data-testid={`reject-btn-${request.request_id}`}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Recusar
                </Button>
              </>
            )}
            
            {request.status === 'accepted' && (
              <>
                <Link to={`/provider/chat/${request.request_id}`} className="flex-1">
                  <Button variant="outline" className="w-full rounded-full">
                    <ChatCircle className="w-4 h-4 mr-2" />
                    Chat
                  </Button>
                </Link>
                <Button 
                  onClick={() => handleCompleteRequest(request.request_id)}
                  className="flex-1 rounded-full"
                  data-testid={`complete-btn-${request.request_id}`}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Concluir
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Header />
      
      <main className="container mx-auto px-4 py-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-2xl font-bold font-outfit mb-6">Pedidos</h1>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-4 w-full mb-6">
              <TabsTrigger value="pending">Pendentes</TabsTrigger>
              <TabsTrigger value="accepted">Aceites</TabsTrigger>
              <TabsTrigger value="completed">Concluídos</TabsTrigger>
              <TabsTrigger value="all">Todos</TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab}>
              {(() => {
                if (loading) {
                  return (
                    <div className="space-y-4">
                      {[1, 2, 3].map((i) => (
                        <Card key={i} className="h-48 animate-pulse bg-muted" />
                      ))}
                    </div>
                  );
                }
                if (filteredRequests.length === 0) {
                  return (
                    <Card>
                      <CardContent className="py-12 text-center">
                        <Clock weight="duotone" className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <h3 className="font-semibold mb-2">Sem pedidos</h3>
                        <p className="text-muted-foreground text-sm">
                          {activeTab === 'pending' 
                            ? 'Não tem pedidos pendentes de momento'
                            : `Não tem pedidos ${REQUEST_STATUS[activeTab]?.label.toLowerCase() || ''}`}
                        </p>
                      </CardContent>
                    </Card>
                  );
                }
                return (
                  <div className="space-y-4">
                    {filteredRequests.map((request) => (
                      <RequestCard key={request.request_id} request={request} />
                    ))}
                  </div>
                );
              })()}
            </TabsContent>
          </Tabs>
        </motion.div>
      </main>

      <MobileNav />
    </div>
  );
}
